import { motion } from "framer-motion";
import { Phone, CalendarDays, User } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import Logo from "./Logo";
import { SEASON_META } from "@/lib/services";
import { useSeason } from "@/lib/season";

const LINKS = [
  { label: "Services", to: "/services", testid: "nav-link-services" },
  { label: "Schedule", to: "/schedule", testid: "nav-link-booking" },
  { label: "About", to: "/about", testid: "nav-link-about" },
];

export default function Nav() {
  const { season, cycleSeason } = useSeason();
  const meta = SEASON_META[season];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-emerald-950/10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link data-testid="nav-logo" to="/" className="flex items-center gap-2.5 shrink-0">
          <Logo size={34} />
          <span className="font-display font-bold tracking-tight text-pine text-sm sm:text-base leading-none">
            ALL-SEASON
            <span className="block text-[10px] tracking-[0.3em] text-limed font-sans font-semibold">CREW</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 relative group ${
                  isActive ? "text-pine" : "text-emerald-950/70 hover:text-pine"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-limed transition-[width] duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <button
            data-testid="nav-season-pill"
            onClick={cycleSeason}
            title="Tap to switch season"
            className="hidden sm:flex items-center gap-2 text-xs font-mono font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border border-emerald-950/10 bg-sage/60 hover:bg-sage transition-colors duration-200"
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.dot }} />
            {meta.label}
          </button>
          <a
            data-testid="nav-phone-btn"
            href="tel:8164009576"
            className="hidden lg:flex items-center gap-2 text-sm font-semibold text-pine hover:text-limed transition-colors duration-200"
          >
            <Phone size={15} strokeWidth={2.5} />
            816-400-9576
          </a>
          <Link
            data-testid="nav-book-cta"
            to="/schedule"
            className="flex items-center gap-2 bg-pine text-bone text-sm font-semibold px-4 py-2 rounded-full hover:bg-limed hover:text-pine-dark transition-colors duration-300"
          >
            <CalendarDays size={15} strokeWidth={2.5} />
            <span className="hidden sm:inline">Book a Slot</span>
            <span className="sm:hidden">Book</span>
          </Link>
          <Link
            data-testid="nav-admin-btn"
            to="/admin"
            title="Crew login"
            className="p-2 rounded-full text-emerald-950/40 hover:text-pine hover:bg-sage transition-colors duration-200"
          >
            <User size={16} />
          </Link>
        </div>
      </div>

      <nav className="md:hidden border-t border-emerald-950/10 bg-white/70">
        <div className="max-w-7xl mx-auto px-5 flex items-center justify-between">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-mobile-${l.testid}`}
              className={({ isActive }) =>
                `py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors duration-200 ${
                  isActive ? "text-pine border-b-2 border-limed" : "text-emerald-950/50"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </motion.header>
  );
}
